import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

/** One crumb in the breadcrumb trail. The last item is usually plain text
 * (no `to`) — see `Breadcrumbs`. */
export interface Breadcrumb {
  label: string;
  to?: string;
}

interface ShellContextValue {
  breadcrumbs: readonly Breadcrumb[];
  setBreadcrumbs: (crumbs: readonly Breadcrumb[]) => void;
}

const ShellContext = createContext<ShellContextValue | null>(null);

/** Holds the current page's breadcrumb trail so `AppLayout`/`ManageLayout`
 * can render it above the content, set by the page itself via
 * `useBreadcrumbs`. Lives inside the layout, not above it — a page outside
 * the shell (auth screens) never needs it. */
export function ShellProvider({ children }: { children: ReactNode }) {
  const [breadcrumbs, setBreadcrumbs] = useState<readonly Breadcrumb[]>([]);
  const value = useMemo(() => ({ breadcrumbs, setBreadcrumbs }), [breadcrumbs]);
  return <ShellContext.Provider value={value}>{children}</ShellContext.Provider>;
}

function useShellContext(): ShellContextValue {
  const value = useContext(ShellContext);
  if (!value) {
    throw new Error('useBreadcrumbs()/Breadcrumbs requires a ShellProvider above it');
  }
  return value;
}

/**
 * Sets the page's breadcrumb trail (§ Content-gebied). Called once per page,
 * near the top of the component — the trail resets automatically as soon as
 * a different page mounts and calls it with its own crumbs.
 */
// eslint-disable-next-line react-refresh/only-export-components -- the hooks belong with their provider
export function useBreadcrumbs(crumbs: readonly Breadcrumb[]): void {
  const { setBreadcrumbs } = useShellContext();
  // Deliberately re-runs on every render where `crumbs` changed by identity;
  // callers pass a fresh array each render (usual React data flow), so this
  // compares by content to avoid an update loop.
  const key = crumbs.map((c) => `${c.label} ${c.to ?? ''}`).join('');
  useEffect(() => {
    setBreadcrumbs(crumbs);
    return () => setBreadcrumbs([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `key` is the content-based dependency
  }, [key]);
}

/** Read-only access to the current trail, for the layout that renders it. */
// eslint-disable-next-line react-refresh/only-export-components -- the hooks belong with their provider
export function useShellBreadcrumbs(): readonly Breadcrumb[] {
  return useShellContext().breadcrumbs;
}
